import { forceSimulation, forceLink, forceManyBody, forceCenter, forceCollide, forceX, forceY } from "d3-force"
import type { Node, Edge } from "@xyflow/react"

// Matches typical node dimensions to prevent overlap
const NODE_RADIUS = 50
// Standard number of iterations for d3-force convergence
const SIMULATION_TICKS = 300

/**
 * Simple string hash function (djb2 algorithm)
 */
function hashCode(str: string): number {
    let hash = 5381
    for (let i = 0; i < str.length; i++) {
        hash = (hash << 5) + hash + str.charCodeAt(i)
    }
    return Math.abs(hash)
}

/**
 * Generate deterministic x,y position based on node ID
 */
function deterministicPosition(id: string): { x: number; y: number } {
    const hashX = hashCode(id)
    const hashY = hashCode(id + "_y")
    return {
        x: (hashX % 1000) / 1000,
        y: (hashY % 1000) / 1000
    }
}

/**
 * Apply force-directed layout to position nodes based on their connections.
 * Nodes with existing positions (non-zero) are kept fixed, and new nodes are positioned relative to them.
 * Relies on graph's fitView to handle final positioning and zoom.
 */
export function applyNetworkLayout<T extends Node>(nodes: T[], edges: Edge[]): T[] {
    if (nodes.length === 0) return nodes

    // Build set of valid node IDs for edge filtering
    const nodeIds = new Set(nodes.map((n) => n.id))

    // Filter edges to only include those where both source and target exist
    const validEdges = edges.filter((e) => nodeIds.has(e.source) && nodeIds.has(e.target))
    const simEdges = validEdges.map((e) => ({ source: e.source, target: e.target }))

    // Create copies because d3-force mutates the objects it receives
    // Nodes with saved positions get fx/fy (fixed), others get deterministic initial position
    const simNodes = nodes.map((n) => {
        const hasPosition = n.position.x !== 0 || n.position.y !== 0
        if (hasPosition) {
            // Fixed position - d3 won't move this node
            return { id: n.id, x: n.position.x, y: n.position.y, fx: n.position.x, fy: n.position.y }
        }
        // Needs positioning - start with deterministic position
        return { id: n.id, ...deterministicPosition(n.id) }
    })

    // Calculate center based on fixed nodes (if any) to position new nodes nearby
    const fixedNodes = simNodes.filter((n) => n.fx !== undefined)
    const centerX = fixedNodes.length > 0 ? fixedNodes.reduce((sum, n) => sum + n.fx!, 0) / fixedNodes.length : 0
    const centerY = fixedNodes.length > 0 ? fixedNodes.reduce((sum, n) => sum + n.fy!, 0) / fixedNodes.length : 0

    const simulation = forceSimulation(simNodes)
        .force(
            "link",
            forceLink(simEdges)
                .id((d: any) => d.id)
                .distance(100)
        )
        .force("charge", forceManyBody().strength(-80))
        .force("center", fixedNodes.length === 0 ? forceCenter() : null)
        .force("collide", forceCollide(NODE_RADIUS))
        .force("x", forceX(centerX).strength(0.03))
        .force("y", forceY(centerY).strength(0.03))
        .stop()

    simulation.tick(SIMULATION_TICKS)

    const positionMap = new Map(simNodes.map((n) => [n.id, { x: n.x, y: n.y }]))

    return nodes.map((node) => ({
        ...node,
        position: positionMap.get(node.id) ?? { x: 0, y: 0 }
    }))
}
